import React, { useState } from 'react';
import { DB } from '../../data/database';
import { getWebsiteUnits, isSold, getUnitName } from '../website/websiteStyles';
import WebsiteCars from '../WebsiteCars';
import CMSSiteContent from './CMSSiteContent';

/* ── Design tokens ── */
const BG = '#FAFAF8';
const BG2 = '#F3F3F1';
const INK = '#14161C';
const INK2 = '#52545C';
const INK3 = '#8B8D94';
const INK4 = '#B0B2B8';
const LINE = '#E5E5E3';
const BLUE = '#2563EB';
const GREEN = '#22C55E';
const RED = '#EF4444';
const AMBER = '#F59E0B';
const heading = '"Inter Tight", sans-serif';
const mono = '"JetBrains Mono", monospace';

/* ── Sidebar icons ── */
const icons = {
  dashboard: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 18, height: 18 }}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  inquiries: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 18, height: 18 }}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
  trade: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 18, height: 18 }}><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="M20 8v6M23 11h-6"/></svg>,
  cars: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 18, height: 18 }}><rect x="3" y="11" width="18" height="7" rx="2"/><path d="M7 11V8a2 2 0 012-2h6a2 2 0 012 2v3"/><circle cx="7.5" cy="18" r="1.5"/><circle cx="16.5" cy="18" r="1.5"/></svg>,
  import: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 18, height: 18 }}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  content: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 18, height: 18 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  translations: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 18, height: 18 }}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>,
  brand: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 18, height: 18 }}><circle cx="12" cy="12" r="3"/><path d="M12 1v4m0 14v4M4.22 4.22l2.83 2.83m9.9 9.9l2.83 2.83M1 12h4m14 0h4M4.22 19.78l2.83-2.83m9.9-9.9l2.83-2.83"/></svg>,
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 18, height: 18 }}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>,
};

const NAV = [
  { id: 'dashboard', label: 'Dashboard', icon: icons.dashboard, group: 'Workspace' },
  { id: 'inquiries', label: 'Inquiries', icon: icons.inquiries, group: 'Workspace' },
  { id: 'trade', label: 'Trade applications', icon: icons.trade, group: 'Workspace' },
  { id: 'cars', label: 'Cars on website', icon: icons.cars, group: 'Catalogue' },
  { id: 'import', label: 'Import from portal', icon: icons.import, group: 'Catalogue' },
  { id: 'content', label: 'Site content', icon: icons.content, group: 'Catalogue' },
  { id: 'brand', label: 'Brand & theme', icon: icons.brand, group: 'Settings' },
];

/* ════════════════════════════════════════════════ */
/*  CMS DASHBOARD                                   */
/* ════════════════════════════════════════════════ */
function CMSDashboard({ user }) {
  const allUnits = getWebsiteUnits();
  const available = allUnits.filter(u => !isSold(u));
  const featured = allUnits.filter(u => u.is_featured);
  const inquiries = []; // placeholder for future inquiry system
  const containers = DB.get('containers') || [];
  const arrivingThisWeek = containers.filter(c => c.status === 'in_transit').length;

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

  const StatCard = ({ label, value, sub, color }) => (
    <div style={{ padding: 20, background: '#fff', borderRadius: 12, border: `1px solid ${LINE}` }}>
      <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK4, marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: heading, fontSize: 32, fontWeight: 700, color: color || INK, letterSpacing: '-0.03em' }}>{value}</div>
      {sub && <div style={{ fontFamily: mono, fontSize: 10, color: GREEN, marginTop: 4 }}>{sub}</div>}
    </div>
  );

  const ActionCard = ({ icon, count, title, desc, color, action }) => (
    <div style={{ padding: 16, background: '#fff', borderRadius: 12, border: `1px solid ${LINE}`, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <div style={{ width: 36, height: 36, borderRadius: 8, background: `${color}12`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: heading, fontSize: 14, fontWeight: 600, color: INK, marginBottom: 2 }}>
          {count > 0 && <span style={{ color }}>{count} </span>}{title}
        </div>
        <div style={{ fontSize: 12, color: INK3, marginBottom: 8 }}>{desc}</div>
        {action && <button style={{ fontFamily: heading, fontSize: 12, fontWeight: 600, color: BLUE, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>{action} →</button>}
      </div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK4, marginBottom: 6 }}>
            {dateStr}
          </div>
          <h1 style={{ fontFamily: heading, fontSize: 28, fontWeight: 700, color: INK, letterSpacing: '-0.03em', marginBottom: 4 }}>
            {greeting}, {user.name?.split(' ')[0] || 'Admin'}
          </h1>
          <p style={{ fontSize: 14, color: INK3 }}>Here's what needs your attention today.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a href="/" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: `1px solid ${LINE}`, background: '#fff', fontFamily: heading, fontSize: 13, fontWeight: 500, color: INK, textDecoration: 'none' }}>
            View site ↗
          </a>
          <button style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, background: BLUE, color: '#fff', fontFamily: heading, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
            Import from portal
          </button>
        </div>
      </div>

      {/* Action cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 24 }}>
        <ActionCard icon="💬" count={inquiries.length} title="new inquiries waiting" desc="Marked as high priority" color={BLUE} action="Open inbox" />
        <ActionCard icon="🤝" count={0} title="buyer applications" desc="Oldest pending 3 days" color={AMBER} action="Review" />
        <ActionCard icon="🚗" count={arrivingThisWeek} title="cars arriving this week" desc="Check shipment status" color={GREEN} action="View shipments" />
        <ActionCard icon="⚠️" count={0} title="content issues" desc="Check for missing images" color={RED} action="Fix" />
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 28 }} className="cms-stats-grid">
        <StatCard label="Units on Website" value={allUnits.length} sub={`↑ ${available.length} available`} />
        <StatCard label="Featured" value={featured.length} />
        <StatCard label="Available" value={available.length} />
        <StatCard label="Sold on site" value={allUnits.filter(u => isSold(u)).length} />
      </div>

      {/* Two columns: stock overview + recent activity */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }} className="cms-two-col">
        {/* Stock breakdown */}
        <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${LINE}`, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ fontFamily: heading, fontSize: 16, fontWeight: 600, color: INK }}>Stock status</div>
            <span style={{ fontFamily: mono, fontSize: 10, color: INK4 }}>{allUnits.length} units total</span>
          </div>
          {(() => {
            const statuses = {};
            allUnits.forEach(u => {
              const s = u.status || 'available';
              statuses[s] = (statuses[s] || 0) + 1;
            });
            const statusColors = { available: GREEN, japan_yard: BLUE, in_transit: AMBER, assigned: '#8B5CF6', sold: RED, reserved: '#F97316' };
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {Object.entries(statuses).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
                  <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColors[status] || INK4, flexShrink: 0 }}></span>
                    <span style={{ flex: 1, fontSize: 13, color: INK2, textTransform: 'capitalize' }}>{status.replace(/_/g, ' ')}</span>
                    <span style={{ fontFamily: mono, fontSize: 13, fontWeight: 600, color: INK }}>{count}</span>
                    <div style={{ width: 80, height: 6, borderRadius: 3, background: BG2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${(count / allUnits.length) * 100}%`, background: statusColors[status] || INK4, borderRadius: 3 }} />
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Recent activity */}
        <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${LINE}`, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ fontFamily: heading, fontSize: 16, fontWeight: 600, color: INK }}>Recent website activity</div>
          </div>
          {allUnits.slice(0, 6).map(u => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${LINE}` }}>
              <div style={{ width: 32, height: 32, borderRadius: 6, background: BG2, overflow: 'hidden', flexShrink: 0 }}>
                {u.images?.[0] ? <img src={u.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 16, display: 'grid', placeItems: 'center', height: '100%' }}>🚗</span>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getUnitName(u)}</div>
                <div style={{ fontSize: 11, color: INK4 }}>{u.sku} · {u.is_featured ? '★ Featured' : 'Listed'}</div>
              </div>
              <span style={{ fontSize: 10, color: u.status === 'available' ? GREEN : INK4, fontFamily: mono, textTransform: 'uppercase' }}>{u.status}</span>
            </div>
          ))}
          {allUnits.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: INK4, fontSize: 14 }}>No units on website yet. Import from portal.</div>}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════ */
/*  CMS CARS ON WEBSITE                             */
/* ════════════════════════════════════════════════ */
function CMSCars() {
  const [search, setSearch] = useState('');
  const [, forceUpdate] = useState();
  const websiteUnits = getWebsiteUnits();

  // All units from portal
  const allVehicles = DB.get('vehicles') || [];
  const allEngines = DB.get('engines') || [];
  const allParts = DB.get('spareParts') || [];
  const allUnits = [
    ...allVehicles.map(u => ({ ...u, _key: 'vehicles', _type: 'vehicle' })),
    ...allEngines.map(u => ({ ...u, _key: 'engines', _type: 'engine' })),
    ...allParts.map(u => ({ ...u, _key: 'spareParts', _type: 'part' })),
  ];

  let filtered = allUnits;
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(u => [u.make, u.model, u.sku, u.partName, u.engineName].filter(Boolean).join(' ').toLowerCase().includes(q));
  }

  const toggle = (unit, field) => {
    const list = DB.get(unit._key) || [];
    const updates = { [field]: !unit[field] };
    if (field === 'is_featured' && !unit.is_featured) updates.show_on_website = true;
    if (field === 'show_on_website' && unit.show_on_website) updates.is_featured = false;
    DB.set(unit._key, list.map(u => u.id === unit.id ? { ...u, ...updates } : u));
    forceUpdate({});
  };

  const onWeb = filtered.filter(u => u.show_on_website);
  const offWeb = filtered.filter(u => !u.show_on_website);

  const UnitRow = ({ unit }) => {
    const name = unit.make ? `${unit.make} ${unit.model || ''}` : unit.engineName || unit.partName || 'Unit';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${LINE}` }}>
        <div style={{ width: 48, height: 36, borderRadius: 6, background: BG2, overflow: 'hidden', flexShrink: 0 }}>
          {unit.images?.[0] ? <img src={unit.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ display: 'grid', placeItems: 'center', height: '100%', fontSize: 14 }}>🚗</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
          <div style={{ fontSize: 11, color: INK4 }}>{unit.sku} · {unit._type}</div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button onClick={() => toggle(unit, 'show_on_website')} style={{
            padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, fontFamily: heading, cursor: 'pointer', border: 'none',
            background: unit.show_on_website ? '#DCFCE7' : BG2, color: unit.show_on_website ? '#16A34A' : INK3,
          }}>
            {unit.show_on_website ? '✓ On Web' : 'Add to Web'}
          </button>
          <button onClick={() => toggle(unit, 'is_featured')} style={{
            padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, fontFamily: heading, cursor: 'pointer', border: 'none',
            background: unit.is_featured ? '#FEF3C7' : BG2, color: unit.is_featured ? '#D97706' : INK3,
          }}>
            {unit.is_featured ? '★ Featured' : '☆ Feature'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontFamily: heading, fontSize: 24, fontWeight: 700, color: INK, letterSpacing: '-0.03em' }}>Cars on website</h2>
          <p style={{ fontSize: 13, color: INK3, marginTop: 4 }}>{onWeb.length} units visible · {onWeb.filter(u => u.is_featured).length} featured</p>
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search units..." style={{
          height: 38, padding: '0 14px', borderRadius: 8, border: `1px solid ${LINE}`, background: '#fff',
          fontFamily: heading, fontSize: 13, color: INK, outline: 'none', width: 240,
        }} />
      </div>

      {/* On website */}
      <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${LINE}`, padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ fontFamily: heading, fontSize: 14, fontWeight: 600, color: INK, marginBottom: 12 }}>
          On website <span style={{ color: INK4, fontWeight: 400 }}>({onWeb.length})</span>
        </div>
        {onWeb.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: INK4, fontSize: 13 }}>No units on website. Use the buttons below to add.</div>
        ) : onWeb.map(u => <UnitRow key={u.id} unit={u} />)}
      </div>

      {/* Not on website */}
      <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${LINE}`, padding: '16px 20px' }}>
        <div style={{ fontFamily: heading, fontSize: 14, fontWeight: 600, color: INK, marginBottom: 12 }}>
          Not on website <span style={{ color: INK4, fontWeight: 400 }}>({offWeb.length})</span>
        </div>
        {offWeb.slice(0, 50).map(u => <UnitRow key={u.id} unit={u} />)}
        {offWeb.length > 50 && <div style={{ padding: 12, textAlign: 'center', color: INK3, fontSize: 12 }}>Showing 50 of {offWeb.length}. Use search to find specific units.</div>}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════ */
/*  CMS SITE CONTENT                                */
/* ════════════════════════════════════════════════ */
function CMSContent() {
  const cfg = DB.get('appConfig') || {};
  const base = typeof cfg === 'object' && !Array.isArray(cfg) ? cfg : {};
  const sc = base.site_content || {};

  const [heroTitle, setHeroTitle] = useState(sc.hero_title || 'Pre-owned, certified, ready to drive.');
  const [heroSub, setHeroSub] = useState(sc.hero_subtitle || 'Hand-picked used vehicles and genuine spare parts, exported worldwide.');
  const [aboutTitle, setAboutTitle] = useState(sc.about_title || 'From a Sharjah yard to ports across the world.');
  const [aboutText, setAboutText] = useState(sc.about_text || '');
  const [saved, setSaved] = useState('');

  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${LINE}`, background: '#fff', fontFamily: heading, fontSize: 14, color: INK, outline: 'none', boxSizing: 'border-box' };

  const save = () => {
    const allCfg = DB.get('appConfig') || {};
    const b = typeof allCfg === 'object' && !Array.isArray(allCfg) ? allCfg : {};
    DB.set('appConfig', { ...b, site_content: { hero_title: heroTitle, hero_subtitle: heroSub, about_title: aboutTitle, about_text: aboutText } });
    setSaved('✓ Saved');
    setTimeout(() => setSaved(''), 3000);
  };

  return (
    <div>
      <h2 style={{ fontFamily: heading, fontSize: 24, fontWeight: 700, color: INK, letterSpacing: '-0.03em', marginBottom: 24 }}>Site content</h2>

      <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${LINE}`, padding: 24, marginBottom: 20 }}>
        <div style={{ fontFamily: heading, fontSize: 16, fontWeight: 600, color: INK, marginBottom: 16 }}>Homepage Hero</div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 6, display: 'block' }}>Headline</label>
          <input value={heroTitle} onChange={e => setHeroTitle(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 6, display: 'block' }}>Subtitle</label>
          <textarea value={heroSub} onChange={e => setHeroSub(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${LINE}`, padding: 24, marginBottom: 20 }}>
        <div style={{ fontFamily: heading, fontSize: 16, fontWeight: 600, color: INK, marginBottom: 16 }}>About Page</div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 6, display: 'block' }}>Title</label>
          <input value={aboutTitle} onChange={e => setAboutTitle(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 6, display: 'block' }}>Description</label>
          <textarea value={aboutText} onChange={e => setAboutText(e.target.value)} rows={5} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button onClick={save} style={{ padding: '10px 24px', borderRadius: 8, background: BLUE, color: '#fff', fontFamily: heading, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>Save Changes</button>
        {saved && <span style={{ fontSize: 13, color: GREEN, fontWeight: 600 }}>{saved}</span>}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════ */
/*  CMS BRAND & THEME                               */
/* ════════════════════════════════════════════════ */
function CMSBrand() {
  const cfg = DB.get('appConfig') || {};
  const base = typeof cfg === 'object' && !Array.isArray(cfg) ? cfg : {};
  const ci = base.company_info || {};
  const hero = base.website_hero || { type: 'animation', overlay_opacity: 0.7 };

  return (
    <div>
      <h2 style={{ fontFamily: heading, fontSize: 24, fontWeight: 700, color: INK, letterSpacing: '-0.03em', marginBottom: 24 }}>Brand & theme</h2>

      <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${LINE}`, padding: 24, marginBottom: 20 }}>
        <div style={{ fontFamily: heading, fontSize: 16, fontWeight: 600, color: INK, marginBottom: 16 }}>Current Settings</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div style={{ fontSize: 12, color: INK4, marginBottom: 6 }}>Company Logo</div>
            {ci.logo_url ? <img src={ci.logo_url} alt="" style={{ height: 48, objectFit: 'contain', background: BG2, borderRadius: 8, padding: 8 }} /> : <div style={{ padding: 16, background: BG2, borderRadius: 8, color: INK4, fontSize: 13 }}>No logo uploaded</div>}
          </div>
          <div>
            <div style={{ fontSize: 12, color: INK4, marginBottom: 6 }}>Hero Background</div>
            <div style={{ padding: 12, background: BG2, borderRadius: 8, fontSize: 13, color: INK2 }}>
              Type: <strong style={{ textTransform: 'capitalize' }}>{hero.type}</strong>
              {hero.type !== 'animation' && <span> · Overlay: {Math.round((hero.overlay_opacity || 0.7) * 100)}%</span>}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: INK4, marginBottom: 6 }}>Company Name</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: INK }}>{ci.name || 'Not set'}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: INK4, marginBottom: 6 }}>Email</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: INK }}>{ci.email || 'Not set'}</div>
          </div>
        </div>
        <div style={{ marginTop: 20, padding: 14, background: '#EFF6FF', borderRadius: 8, fontSize: 13, color: BLUE }}>
          💡 To update logo, company info, or hero background, go to <strong>Settings</strong> in the main portal.
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════ */
/*  PLACEHOLDER PAGES                                */
/* ════════════════════════════════════════════════ */
function Placeholder({ title, desc }) {
  return (
    <div style={{ textAlign: 'center', padding: '80px 20px' }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>🚧</div>
      <div style={{ fontFamily: heading, fontSize: 24, fontWeight: 700, color: INK, marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 14, color: INK3 }}>{desc}</div>
    </div>
  );
}

/* ════════════════════════════════════════════════ */
/*  MAIN CMS LAYOUT                                 */
/* ════════════════════════════════════════════════ */
function CMS({ user }) {
  const [page, setPage] = useState('dashboard');
  const groups = {};
  NAV.forEach(n => { if (!groups[n.group]) groups[n.group] = []; groups[n.group].push(n); });

  const renderPage = () => {
    switch (page) {
      case 'dashboard': return <CMSDashboard user={user} />;
      case 'cars': return <WebsiteCars user={user} />;
      case 'content': return <CMSSiteContent />;
      case 'brand': return <CMSBrand />;
      case 'inquiries': return <Placeholder title="Inquiries" desc="Website inquiry management coming soon." />;
      case 'trade': return <Placeholder title="Buyer Applications" desc="Dealer application review coming soon." />;
      case 'import': return <Placeholder title="Import from Portal" desc="Bulk import units from the main system." />;
      default: return <CMSDashboard user={user} />;
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: 'calc(100vh - 64px)', background: BG, fontFamily: heading }}>
      {/* Sidebar */}
      <div style={{ width: 240, background: '#fff', borderRight: `1px solid ${LINE}`, padding: '24px 0', flexShrink: 0, overflowY: 'auto' }} className="cms-sidebar">
        {Object.entries(groups).map(([group, items]) => (
          <div key={group} style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: INK4, padding: '0 20px', marginBottom: 6 }}>{group}</div>
            {items.map(item => (
              <button key={item.id} onClick={() => setPage(item.id)} style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 20px',
                background: page === item.id ? '#EFF6FF' : 'transparent',
                color: page === item.id ? BLUE : INK2,
                border: 'none', cursor: 'pointer', fontFamily: heading, fontSize: 13,
                fontWeight: page === item.id ? 600 : 400, textAlign: 'left',
                borderRight: page === item.id ? `2px solid ${BLUE}` : '2px solid transparent',
                transition: 'all 0.15s',
              }}
                onMouseEnter={e => { if (page !== item.id) e.target.style.background = BG2; }}
                onMouseLeave={e => { if (page !== item.id) e.target.style.background = 'transparent'; }}>
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Main content */}
      <div style={{ flex: 1, padding: '28px 32px', overflowY: 'auto', minWidth: 0 }}>
        {/* Breadcrumb */}
        <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.1em', color: INK4, marginBottom: 20, textTransform: 'uppercase' }}>
          Workspace › {NAV.find(n => n.id === page)?.label || 'Dashboard'}
        </div>
        {renderPage()}
      </div>

      <style>{`
        @media (max-width: 768px) {
          .cms-sidebar { width: 56px !important; overflow: hidden; }
          .cms-sidebar button span, .cms-sidebar div:first-child { display: none !important; }
          .cms-stats-grid { grid-template-columns: 1fr 1fr !important; }
          .cms-two-col { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

export default CMS;
